import { useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { canViewPage } from "../pageAccess";

// An access-only update must not remount an admitted form or change retry keys.
// Newly opened forbidden pages never mount. Revoked pages stay hidden/blocked
// until access returns or the user explicitly navigates away; server guards
// independently reject operations as soon as the grant is removed.
export default function PageAccessBoundary({ user, path, homeUrl, onRefresh, onLogout, children }) {
  const scope = `${user.merchant_id || user.merchant_code || ""}:${user.id}:${path}`;
  const current = useRef({ scope, admitted: false });
  if (current.current.scope !== scope) current.current = { scope, admitted: false };
  const allowed = canViewPage(user, path);
  if (allowed) current.current.admitted = true;
  const notice = useRef(null);
  useEffect(() => { if (!allowed) notice.current?.focus(); }, [allowed, scope]);
  if (!allowed && !current.current.admitted && homeUrl) return <Navigate to={homeUrl} replace />;
  return <>
    <div key={scope} className="contents" hidden={!allowed} inert={!allowed ? "" : undefined} style={!allowed ? { display: "none" } : undefined}>
      {current.current.admitted ? children : null}
    </div>
    {!allowed && <section ref={notice} tabIndex={-1} role="alert" className="fixed inset-0 z-[10000] bg-background flex items-center justify-center p-8">
      <div className="max-w-lg space-y-4"><h1 className="font-semibold text-xl">Your page access has changed</h1>
        <p>{user.access_error || "This role no longer allows this page. Contact your administrator to restore access."}</p>
        {current.current.admitted && <p className="text-sm text-muted-foreground">Your in-progress form is kept in this tab. Holds keep their original deadlines. Restoring access lets you continue without starting again.</p>}
        <div className="flex flex-wrap gap-4"><button type="button" className="underline" onClick={onRefresh}>Check access</button>
          {homeUrl && <Link className="underline" to={homeUrl}>Open an allowed page</Link>}
          <button type="button" className="underline" onClick={onLogout}>Sign out</button></div>
      </div>
    </section>}
  </>;
}
